import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 cursor-pointer focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-0 focus:border-0 focus:outline-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-ol-primary-foreground hover:bg-info-600/90 px-4",
        destructive:
          "text-destructive-foreground hover:text-destructive/90",
        outline:
          "border !text-white border-input bg-background hover:!bg-gray-300 hover:!text-black dark:hover:bg-hover-color dark:hover:text-accent-foreground",
        secondary:
          "bg-accent text-ol-secondary-foreground hover:bg-ol-secondary/80 px-5",
        login: "w-full border px-0 flex items-center justify-center gap-x-2 bg-background text-color hover:bg-gray-700 hover:text-white dark:hover:bg-gray-100 dark:hover:text-black",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-secondary underline-offset-4 hover:underline",
        navbar: "hover:text-hover-color p-0"
      },
      size: {
        default: "h-10",
        sm: "h-8 rounded-md",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
